import {
  Columns2,
  Database,
  FileText,
  Frame,
  Image,
  Images,
  Minus,
  PanelBottom,
  PanelTop,
  SeparatorHorizontal,
  Signature,
  StickyNote,
  Table,
  Type,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useQuoteCustomFieldNames } from "@/store/quoteSettingsStore";
import { buildFieldBlockHtml, PALETTE_BLOCKS } from "./blockSnippets";
import { hasPageFrame, hasRepeatableChrome } from "./documentEditor";
import { QUOTE_FIELD_OPTIONS } from "./quoteFields";
import type { QuoteFieldOption } from "./quoteFields";

const BLOCK_ICONS: Record<string, LucideIcon> = {
  text: Type,
  columns: Columns2,
  table: Table,
  note: StickyNote,
  signature: Signature,
  image: Image,
  "org-logo": Images,
  divider: Minus,
  "page-break": SeparatorHorizontal,
  "page-frame": Frame,
  markdown: FileText,
  header: PanelTop,
  footer: PanelBottom,
};

interface BlockPaletteProps {
  /** The stored document HTML, for the chrome-presence checks. */
  documentHtml: string;
  /** Inserts the given authored block HTML after the current selection. */
  onInsert: (blockHtml: string) => void;
}

/**
 * Left-sidebar block palette: insert buttons for the block snippets (Text,
 * Columns, Table, Sales rep signature, Image, Organization logo, Divider,
 * Page number, Page break, Page frame, Header, Footer) and one-click insertion of every custom and
 * standard quote field as an fp-field block (custom fields first, as the ones
 * an org reaches for most). Headings and lists are not offered - a text block
 * grows them from their markdown shorthand. Blocks land after the current
 * selection, or at the end of the document (before the repeatable footer) when
 * nothing is selected. Line repeaters are not offered: item rows come from the
 * authored template and are edited as columns. The header and footer entries
 * disable while the document already has one: a document holds at most one of
 * each, and the renderer would silently drop a second.
 * @param documentHtml - The stored document HTML
 * @param onInsert - Callback inserting the authored block HTML
 */
export function BlockPalette({ documentHtml, onInsert }: BlockPaletteProps) {
  const customFieldNames = useQuoteCustomFieldNames();
  const customFieldOptions = customFieldNames.map((name) => ({
    source: `custom:${name}`,
    label: name,
    format: "text" as const,
  }));
  const presentChrome: Record<string, boolean> = {
    header: hasRepeatableChrome(documentHtml, "header"),
    footer: hasRepeatableChrome(documentHtml, "footer"),
    "page-frame": hasPageFrame(documentHtml),
  };
  return (
    <div className="flex flex-col gap-6 p-4">
      <section className="flex flex-col gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          Insert blocks
        </p>
        <div className="flex flex-col gap-1">
          {PALETTE_BLOCKS.map((block) => {
            const Icon = BLOCK_ICONS[block.id] ?? Type;
            const alreadyPresent = presentChrome[block.id] ?? false;
            return (
              // A disabled button is pointer-events-none, so the explanation
              // rides on this wrapper for the tooltip to show.
              <span
                key={block.id}
                className="block"
                title={
                  alreadyPresent
                    ? `The document already has a ${block.label.toLowerCase()}.`
                    : undefined
                }
              >
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-full justify-start gap-2"
                  disabled={alreadyPresent}
                  onClick={() => onInsert(block.html)}
                >
                  <Icon className="size-4 shrink-0" />
                  <span>{block.label}</span>
                </Button>
              </span>
            );
          })}
        </div>
      </section>

      <Separator />

      <FieldSection
        title="Quote fields"
        options={QUOTE_FIELD_OPTIONS}
        onInsert={onInsert}
      />

      {customFieldOptions.length > 0 && (
        <FieldSection
          title="Custom fields"
          options={customFieldOptions}
          onInsert={onInsert}
        />
      )}
    </div>
  );
}

interface FieldSectionProps {
  /** Section heading (e.g. "Quote fields", "Custom fields"). */
  title: string;
  /** The pickable field options rendered as insert buttons. */
  options: readonly QuoteFieldOption[];
  /** Inserts the chosen field's authored block HTML. */
  onInsert: (blockHtml: string) => void;
}

/**
 * One titled group of one-click field-insert buttons in the block palette.
 * @param title - Section heading
 * @param options - The pickable field options
 * @param onInsert - Inserts the chosen field's block HTML
 */
function FieldSection({ title, options, onInsert }: FieldSectionProps) {
  return (
    <section className="flex flex-col gap-2">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </p>
      <div className="flex flex-col gap-1">
        {options.map((option) => (
          <Button
            key={option.source}
            variant="ghost"
            size="sm"
            className="h-8 w-full justify-start gap-2 text-xs"
            onClick={() => onInsert(buildFieldBlockHtml(option.source))}
          >
            <Database className="size-3.5 shrink-0" />
            <span>{option.label}</span>
          </Button>
        ))}
      </div>
    </section>
  );
}
